import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import api from '../services/authService';
import { Pagination as PaginationType } from '../types';
import { colors } from '../styles/colors';
import Pagination from './Pagination';
import ApplyDetailModal from './ApplyDetailModal';

interface Apply {
  id: number;
  opportunity_id: number;
  candidate_id: number;
  salary: number;
  viewed_at: string | null;
  created_at: string;
  opportunity_name: string;
  opportunity_fancy_name: string;
  salary_from: number;
  salary_to: number;
  show_salary: boolean;
  company_id: number;
  candidate_email: string;
  candidate_name: string;
  candidate_lastname: string;
  company_name: string;
  documents_count: number;
  documents: Array<{ id: number; type: string; url: string }>;
}

const Applies: React.FC = () => {
  const navigate = useNavigate();
  const [applies, setApplies] = useState<Apply[]>([]);
  const [pagination, setPagination] = useState<PaginationType>({
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchType, setSearchType] = useState('candidate_email');
  const [filter, setFilter] = useState('all');
  const [openActionMenu, setOpenActionMenu] = useState<number | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedApplyId, setSelectedApplyId] = useState<number | null>(null);

  useEffect(() => {
    fetchApplies();
  }, [pagination.page, searchTerm, filter]);

  const fetchApplies = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
        filter,
        ...(searchTerm && { search: searchTerm, searchType }),
      });

      const response = await api.get(`/applies?${params}`);
      setApplies(response.data.applies);
      setPagination(response.data.pagination);
    } catch (error: any) {
      toast.error('Erro ao carregar candidaturas');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchTerm(search);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleSearchTypeChange = (newType: string) => {
    setSearchType(newType);
    setSearch('');
    setSearchTerm('');
  };

  const handleFilterChange = (newFilter: string) => {
    setFilter(newFilter);
    setSearch('');
    setSearchTerm('');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const formatSalary = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const getCandidateName = (apply: Apply) => {
    if (apply.candidate_name || apply.candidate_lastname) {
      return `${apply.candidate_name || ''} ${apply.candidate_lastname || ''}`.trim();
    }
    return apply.candidate_email;
  };

  const getSearchPlaceholder = () => {
    switch(searchType) {
      case 'candidate_id': return 'ID do candidato';
      case 'candidate_name': return 'nome do candidato';
      case 'candidate_email': return 'email do candidato';
      case 'opportunity_id': return 'ID da vaga';
      case 'opportunity_name': return 'nome da vaga';
      case 'company_name': return 'empresa';
      default: return 'buscar';
    }
  };

  const toggleActionMenu = (applyId: number) => {
    setOpenActionMenu(openActionMenu === applyId ? null : applyId);
  };

  const handleViewDetails = (applyId: number) => {
    setSelectedApplyId(applyId);
    setShowDetailModal(true);
    setOpenActionMenu(null);
  };

  return (
    <div style={styles.container} onClick={() => setOpenActionMenu(null)}>
      <div style={styles.header}>
        <h1 style={styles.title}>Applies</h1>
      </div>

      <div style={styles.scopes}>
        <button 
          onClick={() => handleFilterChange('all')} 
          style={{...styles.scopeBtn, ...(filter === 'all' ? styles.scopeBtnActive : {})}}
        >
          Todas
        </button>
        <button 
          onClick={() => handleFilterChange('published')} 
          style={{...styles.scopeBtn, ...(filter === 'published' ? styles.scopeBtnActive : {})}}
        >
          Publicadas
        </button>
        <button 
          onClick={() => handleFilterChange('trials')} 
          style={{...styles.scopeBtn, ...(filter === 'trials' ? styles.scopeBtnActive : {})}}
        >
          Trials
        </button>
      </div>
      <form onSubmit={handleSearch} style={styles.filters}>
        <select
          value={searchType}
          onChange={(e) => handleSearchTypeChange(e.target.value)}
          style={styles.select}
        >
          <option value="candidate_id">ID Candidato</option>
          <option value="candidate_name">Nome Candidato</option>
          <option value="candidate_email">Email Candidato</option>
          <option value="opportunity_id">ID da Vaga</option>
          <option value="opportunity_name">Nome da Vaga</option>
          <option value="company_name">Empresa</option>
        </select>
        <input
          type="text"
          placeholder={`Buscar por ${getSearchPlaceholder()}...`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={styles.input}
        />
        <button type="submit" style={styles.searchBtn}>
          Buscar
        </button>
      </form>

      {loading ? (
        <div style={styles.loading}>Carregando...</div>
      ) : (
        <>
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.tableHeader}>
                  <th style={styles.th}>Oportunidade</th>
                  <th style={styles.th}>Salário</th>
                  <th style={styles.th}>Candidato</th>
                  <th style={styles.th}>Arquivos</th>
                  <th style={styles.th}>Visualizado?</th>
                  <th style={styles.th}>Created At</th>
                  <th style={styles.th}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {applies.map((apply) => (
                  <tr key={apply.id} style={styles.tableRow}>
                    <td style={styles.td}>
                      <div>
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(`/companies?companyId=${apply.company_id}`);
                          }}
                          style={styles.link}
                        >
                          {apply.company_name}
                        </a>
                        <br />
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(`/opportunities?opportunityId=${apply.opportunity_id}`);
                          }}
                          style={styles.link}
                        >
                          {apply.opportunity_name}
                        </a>
                        {apply.opportunity_fancy_name && (
                          <div style={styles.fancyName}>({apply.opportunity_fancy_name})</div>
                        )}
                      </div>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.salaryContainer}>
                        <div style={styles.salaryRange}>
                          {formatSalary(apply.salary_from)} ~ {formatSalary(apply.salary_to)}
                          {apply.show_salary && ' ☺'}
                        </div>
                        <div style={styles.candidateSalary}>
                          {formatSalary(apply.salary)}
                        </div>
                      </div>
                    </td>
                    <td style={styles.td}>
                      <div>
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(`/candidates?candidateId=${apply.candidate_id}`);
                          }}
                          style={styles.linkBold}
                        >
                          {getCandidateName(apply)}
                        </a>
                        <div style={styles.email}>{apply.candidate_email}</div>
                      </div>
                    </td>
                    <td style={styles.td}>
                      {apply.documents && apply.documents.length > 0 ? (
                        <div style={styles.documentsContainer}>
                          {apply.documents.map((doc) => (
                            <a
                              key={doc.id}
                              href={doc.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={styles.documentLink}
                            >
                              {doc.type || 'Documento'}
                            </a>
                          ))}
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td style={styles.td}>
                      {apply.viewed_at ? (
                        <span style={styles.viewedBadge}>Sim</span>
                      ) : (
                        <span style={styles.notViewedBadge}>Não</span>
                      )}
                    </td>
                    <td style={styles.td}>
                      {new Date(apply.created_at).toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </td>
                    <td style={styles.tdActions}>
                      <div style={styles.actionMenuContainer}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleActionMenu(apply.id);
                          }}
                          style={styles.actionMenuBtn}
                          title="Ações"
                        >
                          ⋮
                        </button>
                        {openActionMenu === apply.id && (
                          <div style={styles.actionMenu}>
                            <button
                              onClick={() => handleViewDetails(apply.id)}
                              style={styles.actionMenuItem}
                            >
                              Ver Detalhes
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={styles.paginationContainer}>
            <div style={styles.paginationInfo}>
              Página {pagination.page} de {pagination.totalPages} ({pagination.total} total)
            </div>
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        </>
      )}
      
      {showDetailModal && selectedApplyId && (
        <ApplyDetailModal
          applyId={selectedApplyId}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedApplyId(null);
          }}
        />
      )}
    </div>
  );
};

const styles = {
  container: {
    width: '100%',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '2rem',
  },
  title: {
    fontSize: '2rem',
    color: colors.primary,
    marginBottom: '2rem',
  },
  filters: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '2rem',
    flexWrap: 'wrap' as const,
  },
  select: {
    padding: '0.75rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '1rem',
    minWidth: '120px',
    backgroundColor: colors.white,
  },
  input: {
    padding: '0.75rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '1rem',
    minWidth: '300px',
  },
  searchBtn: {
    padding: '0.75rem 1.5rem',
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '1rem',
  },
  loading: {
    textAlign: 'center' as const,
    padding: '2rem',
    fontSize: '1.2rem',
    color: colors.textLight,
  },
  tableContainer: {
    backgroundColor: colors.white,
    borderRadius: '8px',
    overflow: 'visible' as const,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    minWidth: '1200px',
    overflow: 'visible' as const,
  },
  tableHeader: {
    backgroundColor: colors.primary,
    color: colors.white,
  },
  th: {
    padding: '1rem',
    textAlign: 'left' as const,
    fontWeight: '600',
  },
  tableRow: {
    borderBottom: `1px solid ${colors.border}`,
  },
  td: {
    padding: '1rem',
    color: colors.text,
  },
  fancyName: {
    fontSize: '0.9rem',
    color: colors.textLight,
    marginTop: '0.25rem',
  },
  salaryContainer: {
    whiteSpace: 'nowrap' as const,
  },
  salaryRange: {
    fontSize: '0.9rem',
    marginBottom: '0.25rem',
  },
  candidateSalary: {
    fontWeight: 'bold' as const,
  },
  email: {
    fontSize: '0.9rem',
    color: colors.textLight,
    marginTop: '0.25rem',
  },
  documentsBadge: {
    backgroundColor: colors.primary,
    color: colors.white,
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: 'bold',
  },
  viewedBadge: {
    backgroundColor: colors.success,
    color: colors.white,
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: 'bold',
  },
  notViewedBadge: {
    backgroundColor: colors.warning,
    color: colors.white,
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: 'bold',
  },
  paginationContainer: {
    marginTop: '2rem',
  },
  paginationInfo: {
    textAlign: 'center' as const,
    color: colors.textLight,
    marginBottom: '1rem',
  },
  scopes: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1rem',
  },
  scopeBtn: {
    padding: '0.5rem 1rem',
    backgroundColor: colors.white,
    color: colors.text,
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '0.9rem',
  },
  scopeBtnActive: {
    backgroundColor: colors.primary,
    color: colors.white,
    borderColor: colors.primary,
  },
  link: {
    color: colors.primary,
    textDecoration: 'none',
    cursor: 'pointer',
  },
  linkBold: {
    color: colors.primary,
    textDecoration: 'none',
    cursor: 'pointer',
    fontWeight: 'bold' as const,
  },
  documentsContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.25rem',
  },
  documentLink: {
    color: colors.primary,
    textDecoration: 'none',
    fontSize: '0.9rem',
    cursor: 'pointer',
  },
  tdActions: {
    padding: '1rem',
    color: colors.text,
    minWidth: '80px',
    position: 'relative' as const,
  },
  actionMenuContainer: {
    position: 'relative' as const,
  },
  actionMenuBtn: {
    padding: '8px 12px',
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 'bold',
  },
  actionMenu: {
    position: 'absolute' as const,
    top: '100%',
    right: 0,
    backgroundColor: colors.white,
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    zIndex: 1001,
    minWidth: '120px',
  },
  actionMenuItem: {
    display: 'block',
    width: '100%',
    padding: '12px 16px',
    backgroundColor: 'transparent',
    border: 'none',
    textAlign: 'left' as const,
    cursor: 'pointer',
    fontSize: '14px',
    color: colors.text,
    borderBottom: `1px solid ${colors.border}`,
    fontFamily: 'inherit',
  },
};

export default Applies;
